import clsx from "clsx";
import type { FC } from "react";

import { AssessmentComment } from "./AssessmentComment";

import { SUBMISSION_ID_QUERY_PARAM } from "components/constants";
import { ContentSidebarHeader } from "components/materials/sidebar/ContentSidebarHeader";
import { ContentSidebarMain } from "components/materials/sidebar/ContentSidebarMain";
import { OverlayContainer } from "components/materials/sidebar/OverlayContainer";
import { PageType } from "components/server-types";
import { t } from "i18n/i18n";
import { OutcomeChip } from "mds/components/OutcomeChip";
import { AssessPointsMarkerIcon } from "mds/icons";
import { useAppSelector } from "store/index";
import {
  NestedPageOutcomeType,
  selectAssessmentBySubmissionId,
  selectFullSubmissionById,
  selectNestedPageOutcomes,
  selectOutcomeAssessmentByAssessmentId,
  selectPageOutcomeRubric,
} from "store/selectors";
import { scoreColor, scoreDescriptionBackgroundColor } from "utils/outcome";
import { useQueryParamsManager } from "utils/routing";

interface AssessmentDisplayViewProps {
  page: PageType;
}

export const AssessmentDisplayView: FC<AssessmentDisplayViewProps> = ({ page }) => {
  const { getQueryParam } = useQueryParamsManager();
  const submissionId = getQueryParam(SUBMISSION_ID_QUERY_PARAM);
  const submission = useAppSelector((s) => selectFullSubmissionById(s, submissionId));
  const assessment =
    useAppSelector((s) => selectAssessmentBySubmissionId(s, submission?.id)) || null;

  const pageOutcomes = useAppSelector((s) => selectNestedPageOutcomes(s, page.id));

  const outcomeAssessments = useAppSelector(
    (s) => selectOutcomeAssessmentByAssessmentId(s, assessment?.id) || [],
  );

  const assessmentItems = pageOutcomes
    .map((pageOutcome) => {
      const outcomeAssessment = outcomeAssessments.find(
        (oa) => oa.page_outcome_id === pageOutcome.id,
      );
      if (!outcomeAssessment) {
        return undefined;
      }
      return {
        pageOutcome,
        score: outcomeAssessment.score,
      };
    })
    .filter((item): item is { pageOutcome: NestedPageOutcomeType; score: number } => Boolean(item));

  return (
    <OverlayContainer className="bg-white">
      <ContentSidebarHeader panelType="overlay" visibleOnMobile>
        <div className="flex items-center gap-2">
          <AssessPointsMarkerIcon className="text-green" />
          <h3 className="m-0">{t("glossary.assessment")}</h3>
        </div>
      </ContentSidebarHeader>

      <ContentSidebarMain bottomPadding={false}>
        <div className="flex flex-col gap-6">
          {assessmentItems.length > 0 && (
            <ol className="flex flex-col gap-6">
              {assessmentItems.map((assessmentItem) => (
                <AssessmentItemView
                  key={assessmentItem.pageOutcome.id}
                  pageOutcome={assessmentItem.pageOutcome}
                  score={assessmentItem.score}
                />
              ))}
            </ol>
          )}

          {page.max_points > 0 && (
            <div className="flex items-center gap-1 text-black-tint-20">
              <span className="font-semibold">{t("submission_assessment.total_points")}:</span>
              <span>
                {assessment?.points}/{page.max_points}
              </span>
            </div>
          )}

          {assessment?.comment && <AssessmentComment comment={assessment.comment} />}
        </div>
      </ContentSidebarMain>
    </OverlayContainer>
  );
};

interface AssessmentItemViewProps {
  pageOutcome: NestedPageOutcomeType;
  score: number;
}

export const AssessmentItemView: FC<AssessmentItemViewProps> = ({ pageOutcome, score }) => {
  // TODO: this is very similar to the OutcomeAssessment component used by AssessmentGradingForm.
  // We should consolidate these implementations. (JN)
  const rubric = useAppSelector((s) => selectPageOutcomeRubric(s, pageOutcome.id));
  const rubricLevel = rubric.find((level) => level.score === score);
  if (!rubricLevel) return null;

  return (
    <li className="flex flex-col gap-2">
      <div className="flex items-center justify-between">
        <OutcomeChip size="m" title={`#${pageOutcome.outcome.title}`} />
        <RubricButtons pageOutcome={pageOutcome} score={score} />
      </div>
      <div
        className={clsx(
          "rounded-lg px-1 py-0.5 text-black-tint-20",
          scoreDescriptionBackgroundColor(score),
        )}
      >
        <div className="flex flex-row items-center">
          <div
            className={clsx(
              "m-1.5 flex size-4 items-center justify-center rounded text-xs text-white",
              scoreColor(score),
            )}
          >
            {score}
          </div>
          <div className="grow font-medium">{rubricLevel.name}</div>
        </div>
        <div className="ml-7 pb-2 pr-1 text-sm font-normal">{rubricLevel.description}</div>
      </div>
    </li>
  );
};

interface RubricButtonsProps {
  pageOutcome: NestedPageOutcomeType;
  score: number;
}

export const RubricButtons: FC<RubricButtonsProps> = ({ pageOutcome, score }) => {
  // TODO: this is very similar to the RubricButtons component used by AssessmentGradingForm.
  // We should consolidate these implementations. (JN)
  const rubric = useAppSelector((s) => selectPageOutcomeRubric(s, pageOutcome.id));

  return (
    <div className="flex items-center rounded-md bg-black-tint-97 text-black-tint-86">
      {rubric.map((rubricLevel) => {
        const highlighted = rubricLevel.score === score;
        return (
          <div
            aria-hidden={!highlighted}
            className={clsx(
              "flex size-7 items-center justify-center rounded-lg",
              highlighted && scoreColor(rubricLevel.score),
              highlighted && "text-white",
            )}
            key={rubricLevel.score}
          >
            {rubricLevel.score}
          </div>
        );
      })}
    </div>
  );
};
